import { BezierConnector } from '@jsplumb/connector-bezier';
import {
  StraightConnector,
  DotEndpoint,
  BehaviouralTypeDescriptor,
  Component,
  REDROP_POLICY_ANY_SOURCE,
  EndpointOptions,
  JsPlumbInstance,
} from '@jsplumb/core';
import { FlowchartConnector } from '@jsplumb/connector-flowchart';
import {
  AnchorLocations,
  AnchorSpec,
  ConnectorSpec,
  EndpointSpec,
  EndpointStyle,
  OverlaySpec,
  PaintStyle,
} from '@jsplumb/common';
import {
  DragOptions,
  BrowserJsPlumbDefaults,
  newInstance,
} from '@jsplumb/browser-ui';
import _ from 'lodash';
import { DEVICE_MODE } from './lib/pointerDeviceDetector.js';

// Current View constants
export const VIEW = Object.freeze({
  PROJECT_VIEW: 'PROJECT_VIEW', // Window open at project view
  SELECT_PROJECT_VIEW: 'SELECT_PROJECT_VIEW', // Window open at select project view
  ACCOUNT_VIEW: 'ACCOUNT_VIEW', // Window open at account view
});

export const PANZOOM = Object.freeze({
  CURSOR_MODE: { SELECT: 'SELECT', GRAB: 'GRAB' },
  MOUSE_MODE: { ...DEVICE_MODE },
  // BOARD_WIDTH: 5000,                              // The board width and height
  // BOARD_HEIGHT: 5000,
  // Margins for fit to screen when viewing
  FIT_TO_VIEWPORT_MARGINS: { left: 20, right: 20, top: 20, bottom: 20 },
  // Scale limits for panzoom when viewing
  SCALE_LIMITS: { min: 0.15, max: 4, step: 0.1 },
  ZOOMINOUT_INCREMENT: 0.5, // The  percentage of increment or decrement in zoom in/out buttons
  URL_SCALE_DIGITS: 6, // The number of digits to appear after the decimal point
  // Marigns for fit to screen when calling puppeteer to export in pdf or image
  FIT_TO_VIEWPORT_EXPORT_MARGINS: { left: 40, right: 40, top: 40, bottom: 40 },
  // Scale limits for panzoom when exporting
  SCALE_LIMITS_EXPORT: { min: 0, max: 10, step: 0.1 },
});

export const EVENT = Object.freeze({
  OPEN_CONTEXT_MENU: 'OPEN_CONTEXT_MENU', // @caller, @event
  EDIT_COMPONENT: 'EDIT_COMPONENT',
  DISABLE_LIST_ITEM: ' DISABLE_LIST_ITEM',
  ENABLE_LIST_ITEM: 'ENABLE_LIST_ITEM',
  CLEAR_SELECTIONS: 'CLEAR_SELECTIONS',
  DRAGGING_COMPONENT: 'DRAGGING_COMPONENT',
  DRAGGING_ASSET: 'DRAGGING_ASSET',
  DRAGGING_AUDIO_ASSET: 'DRAGGING_AUDIO_ASSET',
  CLICK_ELEMENT: 'CLICK_ELEMENT', // @parameters: elementId, event
  CLICK_CONNECTION: 'CLICK_CONNECTION', // @paremeters: connectionId
  CLICK_ENDPOINT: 'CLICK_ENDPOINT', // @parameters: endpoint
  RIGHT_CLICK_CONNECTION: 'RIGHT_CLICK_CONNECTION', // @paremeters: connectionId
  DBL_CLICK_CONNECTION: 'DBL_CLICK_CONNECTION', // @parameters: connectionId, event
  DRAG_CONNECTION: 'DRAG_CONNECTION', // @parameters: connectionId
  DELETE_SELECTIONS: 'DELETE_SELECTIONS', // @parameters: none
  SELECT_ALL: 'SELECT_ALL', // @parameters: none
  COPY: 'COPY',
  CUT: 'CUT',
  PASTE: 'PASTE',
  SHIFT_PASTE: 'SHIFT_PASTE',
  RESETZOOM: 'RESETZOOM',
  ZOOMTOBOARD: 'ZOOMTOBOARD',
  ZOOMTONODE: 'ZOOMTONODE',
  ZOOMIN: 'ZOOMIN',
  ZOOMOUT: 'ZOOMOUT',
  PANZOOMTOTRANSFORM: 'PANZOOMTOTRANSFORM',
  FIT_TO_SCREEN: 'FIT_TO_SCREEN',
  CLEAR_DRAG_SELECTION: 'CLEAR_DRAG_SELECTION',
  MENTION_CHANGE: 'MENTION_CHANGE', // @parameters: a mention object
  DELETE_PROJECT_MODAL: 'DELETE_PROJECT_MODAL', // @parameters: project hash
  ESC_BOARD: 'ESC_BOARD',
  ARROW_KEY_STATE_CHANGE: 'ARROW_KEY_STATE_CHANGE', // @parameters: an object { x, y } with the normalized vector of key change
  PROJECT_IMPORT_COMPLETED: 'PROJECT_IMPORT_COMPLETED',
  OPEN_FILE_UPLOADER: 'OPEN_FILE_UPLOADER', // @parameters: assetId, addEnd, replace
  LIMIT_MODAL: 'LIMIT_MODAL',
});

export const ARROW_KEY_ANIMATIONS = Object.freeze({
  PAN_RATE: 10, // After how many msec we re-render pan
  PAN_SPEED: 4, // How many pixels we are moving on each pan render
  NUDGE_RATE: 100, // After how many msec we render nudge
  NUDGE_FIRST_RATE: 500, // For the first frame, we need another delay
  NUDGE_SPEED: 1, // How many pixels we are moving on each nudge render (if no grid snap)
  NUDGE_FAST_SPEED: 20, // How many pixels we are moving on each nudge (using shift)
});

// The following are the types of dragged items throughout the application
export const DRAG_TYPE = Object.freeze({
  COMPONENT: 'COMPONENT',
  CONTAINER: 'CONTAINER',
  ELEMENT_COMPONENT: 'ELEMENT_COMPONENT',
  ELEMENT_ASSET: 'ELEMENT_ASSET',
  ELEMENT_ASSET_AUDIO: 'ELEMENT_ASSET_AUDIO',
  ATTRIBUTE_COMPONENT: 'ATTRIBUTE_COMPONENT',
  ATTRIBUTE_ASSET: 'ATTRIBUTE_ASSET',
  NEW_ELEMENT: 'NEW_ELEMENT',
  NEW_JUMPER: 'NEW_JUMPER',
  NEW_BRANCH: 'NEW_BRANCH',
  NEW_NOTE: 'NEW_NOTE',
  NEW_COMMENT: 'NEW_COMMENT',
  BOARD: 'BOARD',
  BOARD_ELEMENT: 'BOARD_ELEMENT',
  BOARD_CONTAINER: 'BOARD_CONTAINER',
  ASSET: 'ASSET',
  ASSET_AUDIO: 'ASSET_AUDIO',
  SEARCH_ELEMENT: 'SEARCH_ELEMENT',
  SEARCH_COMPONENT: 'SEARCH_COMPONENT',
  SEARCH_ASSET: 'SEARCH_ASSET',
});

export const DRAG_TYPES_NEW = [
  DRAG_TYPE.NEW_ELEMENT,
  DRAG_TYPE.NEW_NOTE,
  DRAG_TYPE.NEW_JUMPER,
  DRAG_TYPE.NEW_BRANCH,
  DRAG_TYPE.NEW_COMMENT,
];

export const DRAG_LIST = Object.freeze({
  COMPONENTS: 'COMPONENTS',
  ATTRIBUTES: 'ATTRIBUTES',
  ELEMENTS: 'ELEMENTS',
  JUMPERS: 'JUMPERS',
  BOARDS: 'BOARDS',
  BOARD: 'BOARD',
  ASSETS: 'ASSET',
  SEARCH: 'SEARCH',
});

export const NOTIFICATION_TYPE = Object.freeze({
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
});

/**
 * Jsplumb-related settings
 */
export const JSPLUMB = Object.freeze({
  /**
   * Configure the default connectors that we use
   * @type {Object}
   */
  connectors: {
    /**
     * Configure default Bezier connector
     * @type {ConnectorSpec}
     */
    Bezier: { type: BezierConnector.type, options: { curviness: 80 } },

    /**
     * Configure default straight connector
     * @type {ConnectorSpec}
     */
    Straight: { type: StraightConnector.type, options: { stub: 0, gap: 0 } },

    /**
     * Configure default flowchart connector
     * @type {ConnectorSpec}
     */
    Flowchart: {
      type: FlowchartConnector.type,
      options: {
        stub: 30,
        alwaysRespectStubs: false,
        gap: 0,
        midpoint: 0.5,
        cornerRadius: 0,
      },
    },
  },

  /**
   * Configure the source endpoint for a connection
   * @type {EndpointSpec}
   */
  sourceEndpoint: {
    type: DotEndpoint.type,
    options: {
      cssClass: 'source',
      hoverClass: 'source-hover',
      radius: 5,
      deleteOnEmpty: true,
    },
  },

  /**
   * Configure the style of the source endpoint.
   * @type {EndpointStyle}
   */
  sourceEndpointStyle: {
    fill: '#f3f3f3',
    stroke: 'transparent',
    strokeWidth: 10,
  },

  /**
   * Configure the target endpoint for a connection
   * @type {EndpointSpec}
   */
  targetEndpoint: {
    type: DotEndpoint.type,
    options: {
      cssClass: 'target',
      hoverClass: 'target-hover',
      deleteOnEmpty: true,
    },
  },

  /**
   * Configure the style of the target endpoint
   * @type {EndpointStyle}
   */
  targetEndpointStyle: {
    fill: 'transparent',
    stroke: 'transparent',
    strokeWidth: 10,
  },

  /**
   * Configure the style of the source endpoint from condition
   * @type {EndpointOptions}
   */
  sourceEnpointCondition: {
    /** @type {EndpointSpec} */
    endpoint: {
      type: DotEndpoint.type,
      options: {
        cssClass: 'condition-source',
      },
    },

    /** @type {PaintStyle} */
    paintStyle: {
      fill: '#f3f3f3',
      stroke: 'transparent',
      strokeWidth: 10,
    },

    /** @type {boolean} */
    source: true,

    /** @type {AnchorSpec} */
    anchor: AnchorLocations.Right,
  },

  /**
   * Configure the default anchor (Continuous on all faces).
   * @type {AnchorSpec}
   */
  defaultAnchor: AnchorLocations.Continuous,

  /**
   * Configure the anchor for target branches.
   * @type {AnchorSpec}
   */
  targetBranchAnchor: {
    type: AnchorLocations.Continuous,
    options: { faces: ['left', 'top', 'bottom'] },
  },

  /**
   * The source condition has a static anchor on the right side
   * @type {AnchorSpec}
   */
  sourceConditionAnchor: AnchorLocations.Right,

  /**
   * Configure the arrow overlay.
   * @type {OverlaySpec}
   */
  arrowOverlay: {
    type: 'Arrow',
    options: {
      cssClass: 'arrow',
      location: 1,
      id: 'arrow',
      length: 8,
      width: 12,
      foldback: 1.0,
      paintStyle: {
        lineWidth: 2,
        strokeWidth: 0,
        stroke: '#f3f3f3',
        fill: '#f3f3f3',
      },
    },
  },

  /**
   * Configure default source selector options.
   * @type {BehaviouralTypeDescriptor}
   */
  get sourceSelectorOptions() {
    return {
      endpoint: this.sourceEndpoint,
      endpointStyle: this.sourceEndpointStyle,
      extract: { 'data-handler': 'handler' }, // Custom data for the face (assigned to endpoint)
      redrop: REDROP_POLICY_ANY_SOURCE,
    };
  },

  /**
   * Configure default target selector options.
   * @type {BehaviouralTypeDescriptor}
   */
  get targetSelectorOptions() {
    return {
      endpoint: this.targetEndpoint,
      endpointStyle: this.targetEndpointStyle,
      rank: 0,
    };
  },

  /**
   * Configure branch target selector options.
   * @type {BehaviouralTypeDescriptor}
   */
  get branchTargetSelectorOptions() {
    return {
      ...this.targetSelectorOptions,
      anchor: this.targetBranchAnchor,
    };
  },

  /**
   * Configure the source selector
   * @returns {BehaviouralTypeDescriptor}
   */
  getSourceFaceSelectorOptions: () => {
    return {
      ...JSPLUMB.sourceSelectorOptions,
      extract: { 'data-handler': 'handler' }, // Custom data for the face (assigned to endpoint)
      anchor: {
        type: AnchorLocations.Continuous,
        options: { faces: ['left', 'right', 'top', 'bottom'] }, // [face] },
      },
    };
  },

  /**
   * Configure a specific face selector options
   * @param {String} face One of 'left', 'right', 'top', 'bottom'
   * @returns {BehaviouralTypeDescriptor}
   */
  getFaceSelectorOptions: face => {
    return {
      ...JSPLUMB.targetSelectorOptions,
      extract: { 'data-handler': 'handler' }, // Custom data for the face (assigned to endpoint)
      anchor: {
        type: AnchorLocations.Continuous,
        options: { faces: [face] },
      },
      rank: 1,
    };
  },

  /**
   * Configure a specific face selector options
   * @returns {BehaviouralTypeDescriptor}
   */
  getCenterSelectorOptions: () => {
    return {
      ...JSPLUMB.targetSelectorOptions,
      extract: { 'data-handler': 'handler' }, // Custom data for the face (assigned to endpoint)
      anchor: {
        type: AnchorLocations.Continuous,
        options: { faces: ['left', 'right', 'top', 'bottom'] },
      },
    };
  },

  /**
   * Configure default jsplumb options.
   * @type {BrowserJsPlumbDefaults}
   */
  get defaults() {
    return {
      /**
       * Configure the anchor for any endpoint for which no anchor is declared (both source and target for connections).
       * @type {AnchorSpec}
       */
      anchor: JSPLUMB.defaultAnchor,

      /**
       * Configure the default connector to use.
       * @type {ConnectorSpec}
       */
      connector: JSPLUMB.connectors.Bezier,

      /**
       * Configure default endpoint styles. Useful for configuring unattached endpoints also.
       * @type {EndpointStyle}
       */
      endpointStyle: { fill: '#f3f3f3' },

      /**
       * Configure default source and target endpoints.
       * @type {[EndpointSpec, EndpointSpec]}
       */
      endpoints: [JSPLUMB.sourceEndpoint, JSPLUMB.targetEndpoint],

      /**
       * Configure default source and target endpoint styles.
       * @type {[EndpointStyle, EndpointStyle]}
       */
      endpointStyles: [
        JSPLUMB.sourceEndpointStyle,
        JSPLUMB.targetEndpointStyle,
      ],

      /**
       * Configure the default style of a connector.
       * @type {PaintStyle}
       */
      paintStyle: {
        strokeWidth: 2,
        stroke: '#f3f3f3',
        outlineStroke: 'transparent',
        outlineWidth: 10,
      },

      /**
       * Τhe default overlays to attach to every connection - an arrow overlay.
       * @type {Array<OverlaySpec>}
       */
      connectionOverlays: [JSPLUMB.arrowOverlay],
    };
  },

  /**
   * Configure the initial options for jsplumb by taking into account the container element and the grid.
   * @param {HTMLElement} container
   * @param {Object} grid
   * @returns {BrowserJsPlumbDefaults}
   */
  getInitialOptions: (container, grid) => {
    return {
      ...JSPLUMB.defaults,
      container,
      dragOptions: { ...JSPLUMB.defaults.dragOptions, grid },
    };
  },

  /**
   * Configure the connect options for jsplumb
   */
  connect: (source, target, type, theme, sourceAnchor, targetAnchor) => {
    // Configure the connector given the type and the theme
    const connector = _.cloneDeep(JSPLUMB.connectors[type]);
    if (theme) {
      connector.options.cssClass = `theme-${theme}`;
    }
    return {
      source,
      target,
      detachable: true,
      connector,
      endpoints: [JSPLUMB.sourceEndpoint, JSPLUMB.targetEndpoint],
      anchors: [sourceAnchor, targetAnchor],
    };
  },

  /**
   * Utility to remove a class with pattern from a jsplumb component
   * @param {Component} component
   * @param {string} pattern
   * @param {boolean} cascade
   */
  removeClass: (component, pattern, cascade) => {
    const classes = component.getClass().split(' ').filter(Boolean);

    classes.forEach(c => {
      if (c.startsWith(pattern)) {
        component.removeClass(c, cascade);
      }
    });
  },

  /**
   * Create the jsplumb instance and initialize jsplumb selectors
   * @param {HTMLElement} container The jsplumb container element
   * @param {Object} grid The grid options
   * @param {Object} defaultStyle The default connection style
   * @returns {JsPlumbInstance}
   */
  setupInstance: (container, grid, defaultStyle) => {
    const {
      getInitialOptions,
      getSourceFaceSelectorOptions,
      getFaceSelectorOptions,
      sourceSelectorOptions,
      targetSelectorOptions,
      branchTargetSelectorOptions,
      connectors,
    } = JSPLUMB;

    // Initialize jsplumb and set up defaults
    const instance = newInstance(getInitialOptions(container, grid));

    // Set up the default connection style
    const { type, theme } = defaultStyle;
    const connector = connectors[type];
    if (theme) {
      connector.options.cssClass = `theme-${theme}`;
    }
    instance.importDefaults({ connector });

    // Set the source selector for connection handlers for elements
    const sourceFaceOptions = getSourceFaceSelectorOptions();
    const elementHandler = '.plank .connection-handler';
    const faces = ['left', 'right', 'up', 'down'];
    faces.forEach(face => {
      const selector = `${elementHandler}.${face}`;
      instance.addSourceSelector(selector, sourceFaceOptions);
    });

    // Set the target selector for full element
    instance.addTargetSelector('.plank', targetSelectorOptions);

    // Set the target selector for jumpers
    instance.addTargetSelector('.jumper', targetSelectorOptions);

    // Set the target selector for branches
    instance.addTargetSelector('.branch', branchTargetSelectorOptions);

    // Set the target selector for element left target
    instance.addTargetSelector(
      '.connection-handler.left',
      getFaceSelectorOptions('left')
    );

    // Set the target selector for element right target
    instance.addTargetSelector(
      '.connection-handler.right',
      getFaceSelectorOptions('right')
    );

    // Set the target selector for element top target
    instance.addTargetSelector(
      '.connection-handler.up',
      getFaceSelectorOptions('top')
    );

    // Set the target selector for element bottom target
    instance.addTargetSelector(
      '.connection-handler.down',
      getFaceSelectorOptions('bottom')
    );

    return instance;
  },
});

export const VARIABLES = Object.freeze({
  INT: { type: 'integer', value: 0 },
  NUMBER: { type: 'float', value: 0.0 },
  STRING: { type: 'string', value: '' },
  BOOLEAN: { type: 'boolean', value: false },
});

export const SYS_PERMISSIONS = Object.freeze({
  SYS_WORKSPACE_MANAGE: 'sys:workspace:manage',
  SYS_MEMBERSHIPS_READ: 'sys:memberships:read',
  SYS_MEMBERSHIPS_MANAGE: 'sys:memberships:manage',
  SYS_MEMBERSHIPS_DELETE: 'sys:memberships:delete',
  SYS_PROJECTS_READ: 'sys:projects:read',
  SYS_PROJECTS_COMMENT: 'sys:projects:comment',
  SYS_PROJECTS_EDIT: 'sys:projects:edit',
  SYS_PROJECTS_DUPLICATE: 'sys:projects:duplicate',
  SYS_PROJECTS_MANAGE: 'sys:projects:manage',
  SYS_PROJECTS_DELETE: 'sys:projects:delete',
  SYS_PROJECTS_CREATE: 'sys:projects:create',
  SYS_PROJECTS_EXPORT: 'sys:projects:export',
  SYS_PROJECTS_EXPORT_BACKUP: 'sys:projects:export_backup',
  SYS_PROJECTS_IMPORT: 'sys:projects:import',
  SYS_ROLES_MANAGE: 'sys:roles:manage',
  SYS_API_READ: 'sys:api:read',
  SYS_API_CREATE: 'sys:api:create',
  SYS_API_MANAGE: 'sys:api:manage',
  SYS_BILLING_MANAGE: 'sys:billing:manage',
});

export const FEATURE_FLAGS = Object.freeze({
  AI_ELEMENT_GENERATOR: 'ai_element_generator',
  AI_ELEMENT_ENHANCER: 'ai_element_enhancer',
  ASSET_LISTS: 'asset_lists',
});
